import * as React from "react";
import PropTypes from "prop-types";
import Badge from "nx-components/Badge";
import prefixStyles from "nx-components/utils/prefixStyles";
import CustomPropTypes from "nexia_mobile/CustomPropTypes";

export function truncateAndCombineIcons(values, unique) {
  let icons;
  if (unique) {
    // discard repeated values and update count
    icons = uniqueifyWhileCounting(values);
  } else {
    icons = uniqueifyKeys(values);
  }

  // only support up to 6 icons
  return icons.slice(0, 6);
}

function uniqueifyKeys(values) {
  return values.map((value, index) => {
    value.count = 1;
    value.key += index;
    return value;
  });
}

function uniqueifyWhileCounting(icons) {
  const values = [];
  const counts = {};
  icons.forEach((icon) => {
    if (!counts.hasOwnProperty(icon.key)) {
      counts[icon.key] = 0;
      values.push(icon); // can't use _.uniq with objects (===)
    }
    counts[icon.key] += 1;
  });
  return values.map((icon) => {
    icon.count = counts[icon.key]; // adjust count with new data
    return icon;
  });
}

function IconGroup({ className, values, unique, disableBadges, style }) {
  if (!values || values.length === 0) {
    return null;
  }

  const icons = truncateAndCombineIcons(values, unique);

  const styles = prefixStyles({
    root: {
      display: "flex",
      flexWrap: "wrap",
      position: "relative",
      margin: "0.1em auto",
      justifyContent: "center",
      lineHeight: 0,
      ...style,
    },
    iconBadgeWrapper: {
      position: "relative",
    },
    icon: {
      lineHeight: 0,
      margin: icons.length > 1 ? "0.2em" : "0.1em",
      fontSize: "0.5em",
      width:
        // For non-thermostat icons, only increase the width to match
        // thermostat icon width if there's an even number of icons greater
        // than 2, because the only reason we match the width is so that the
        // icons align vertically, but there's no vertical break when there's
        // only two icons, and alignment doesn't matter when we have an odd
        // number of icons, in fact the extra margin actually looks worse in
        // some cases.
        icons.length > 2 &&
        icons.length % 2 === 0 &&
        icons.some((icon) => icon.name === "thermostat")
          ? "1.5em"
          : "1em",
    },
    thermostatIcon: {
      // The width for a thermostat icon is always 1.5em to accomodate the
      // temperature number next to the icon.
      width: "1.5em",
    },
    badge: {
      fontSize: "0.6rem",
    },
  });

  let fontSize = "";
  switch (icons.length) {
    case 2: {
      fontSize = "0.7em";
      break;
    }
    case 3:
    case 4: {
      fontSize = "0.6em";
      break;
    }
    case 5:
    case 6: {
      fontSize = "0.5em";
      break;
    }
  }

  return (
    <div
      className={className}
      style={styles.root}
      data-component-name="IconGroup"
      {...(values.length > 1
        ? {
            // Don't read Icon content when there's more than one icon in the
            // group, because some of those Icons might have temperatures, and
            // those temperature values don't really make sense in the tile
            // context without describing all the other icons. However, when
            // there's only one Icon in the group, we want to read temperatures,
            // because it makes sense at that level.
            "aria-hidden": true,
          }
        : null)}
    >
      {icons.map((icon) => {
        const iconStyle = {
          ...styles.icon,
          ...(icon.name === "thermostat" ? styles.thermostatIcon : null),
          fontSize,
        };

        if (icon.count > 1 && !disableBadges) {
          return (
            <div key={icon.key} style={styles.iconBadgeWrapper}>
              <Badge style={styles.badge}>{icon.count}</Badge>
              {React.cloneElement(icon.element, { style: iconStyle })}
            </div>
          );
        } else {
          return React.cloneElement(icon.element, {
            style: iconStyle,
            key: icon.key,
          });
        }
      })}
    </div>
  );
}
IconGroup.propTypes = {
  className: PropTypes.string,
  values: CustomPropTypes.keyedElementList.isRequired,
  unique: PropTypes.bool,
  disableBadges: PropTypes.bool,
  style: PropTypes.object,
};
IconGroup.defaultProps = {
  unique: true,
};

export default IconGroup;



// WEBPACK FOOTER //
// ./src/components/IconGroup.jsx